//
// --- Types ---

export type BaseListCategoryType = {
  id: ListCategoryIdType;
  label: string;
};

export type BidEventItemListCategoryType = BaseListCategoryType;

export type GlobalSupplierListCategoryItemType = BaseListCategoryType & {
  globalSupplierListCategory: string;
  globalSupplierListType: number;
};

export type ListCategoryMapItemType =
  | BidEventItemListCategoryType
  | GlobalSupplierListCategoryItemType
  | PowerScoreContactListCategoryType;

export type ListCategoryMapType = {
  [key in ListCategoryIdType]: ListCategoryMapItemType;
};

export type PowerScoreContactListCategoryType = BaseListCategoryType;

export type GlobalSupplierListAccessKeyType = keyof typeof GLOBAL_SUPPLIER_LIST_ACCESS;
export type GlobalSupplierListAccessType = typeof GLOBAL_SUPPLIER_LIST_ACCESS[GlobalSupplierListAccessKeyType];

export type GlobalSupplierListPermissionGroupKeyType = keyof typeof GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP;
export type GlobalSupplierListPermissionGroupType =
  typeof GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP[GlobalSupplierListPermissionGroupKeyType];

export type ListCategoryIdKeyType = keyof typeof LIST_CATEGORY_ID;
export type ListCategoryIdType = typeof LIST_CATEGORY_ID[ListCategoryIdKeyType];

//
// --- Type Guard ---

export function isGlobalSupplierListCategoryItem(
  listCategoryItem: ListCategoryMapItemType
): listCategoryItem is GlobalSupplierListCategoryItemType {
  return typeof (listCategoryItem as GlobalSupplierListCategoryItemType).globalSupplierListCategory !== 'undefined';
}

//
// --- Constants ---

export const LIST_CATEGORY_ID = {
  BID_EVENT_ITEMS_LIST: 'BID_EVENT_ITEMS_LIST',
  COMPANY_LISTS: 'COMPANY_LISTS',
  MY_LISTS: 'MY_LISTS',
  OTHER_COMPANY_LISTS: 'OTHER_COMPANY_LISTS',
  POWER_SCORE_CONTACTS: 'POWER_SCORE_CONTACTS'
} as const;

//
// --- Individual List Category Items ---

export const bidEventItemsListCategoryItem: BidEventItemListCategoryType = {
  id: LIST_CATEGORY_ID.BID_EVENT_ITEMS_LIST,
  label: 'Event Item Lists'
};

export const companyListsCategoryItem: GlobalSupplierListCategoryItemType = {
  globalSupplierListCategory: 'myCompanysLists',
  globalSupplierListType: 1,
  id: LIST_CATEGORY_ID.COMPANY_LISTS,
  label: 'Company Lists'
};

export const myListsCategoryItem: GlobalSupplierListCategoryItemType = {
  globalSupplierListCategory: 'myLists',
  globalSupplierListType: 1,
  id: LIST_CATEGORY_ID.MY_LISTS,
  label: 'My Lists'
};

export const otherCompanyListsCategoryItem: GlobalSupplierListCategoryItemType = {
  globalSupplierListCategory: 'otherCompaniesLists',
  globalSupplierListType: 1,
  id: LIST_CATEGORY_ID.OTHER_COMPANY_LISTS,
  label: 'Other Company Lists'
};

export const powerScoreContactsListCategoryItem: PowerScoreContactListCategoryType = {
  id: LIST_CATEGORY_ID.POWER_SCORE_CONTACTS,
  label: 'Frequent Contacts'
};

//
// --- List Category Maps ---

export const ListCategoryMap: ListCategoryMapType = {
  [LIST_CATEGORY_ID.BID_EVENT_ITEMS_LIST]: { ...bidEventItemsListCategoryItem },
  [LIST_CATEGORY_ID.COMPANY_LISTS]: { ...companyListsCategoryItem },
  [LIST_CATEGORY_ID.MY_LISTS]: { ...myListsCategoryItem },
  [LIST_CATEGORY_ID.OTHER_COMPANY_LISTS]: { ...otherCompanyListsCategoryItem },
  [LIST_CATEGORY_ID.POWER_SCORE_CONTACTS]: { ...powerScoreContactsListCategoryItem }
};

//
// --- Discovery List Access ---

export const GLOBAL_SUPPLIER_LIST_ACCESS = {
  ME_ONLY: 0,
  MY_COMPANY: 1,
  SELECTED_COMPANY: 2
} as const;

//
// --- Global Supplier List Permissions ---

export const GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP = {
  ALL: 'all',
  COMPANY_OWNER: 'company-owner',
  OWNER: 'owner'
} as const;
